import { useEffect, useRef, useState } from 'react';
import { Button, Card, Form } from 'antd';
import { observer } from 'mobx-react-lite';
import moment from 'moment';

import { useResource } from 'app/common/api';
import { usePageNotify } from 'app/common/notifications/features/page-notifications';
import { CombinedKeyMap } from 'app/libs/combined-key-map/combined-key-map';
import { useInstance } from 'app/libs/use-instance';
import { CanAccess } from 'app/railways/domains/access/features/permissions';
import { Loading } from 'app/ui';

import {
  TTariffProfile,
  TTariffProfileRule,
} from '../../../core/tariff-profile';
import { TTariffProfileGrid } from '../../../core/tariff-profile-grid';
import { getTariffProfileRequest } from '../../../requests/get-tariff-profile.request';
import { updateTariffProfileRequest } from '../../../requests/update-tariff-profile.request';
import { TariffProfileFields } from '..//tariff-profile-fields/tariff-profile.fields';
import {
  ChangedCellsProvider,
  createChangeCellState,
} from '../discount-rules-grid/changed-cells-provider';
import { DiscountRulesGrid } from '../discount-rules-grid/discount-rules-grid';
import { TariffProfileRulesRecord } from '../discount-rules-grid/types';
import { extractMetaFormFields } from '../tariff-profile-fields/extract-meta-form-fields';
import { TTariffProfileMetaFormFields } from '../tariff-profile-fields/types';

type DetailsTariffProfileProps = {
  tariffProfileId: TTariffProfile['id'];
};

const prepareTariffProfileData = (data: TTariffProfileGrid) => {
  const tariffProfileMetaFields = extractMetaFormFields(data.tariffProfile);

  return { tariffProfileMetaFields };
};

export const DetailsTariffProfile = observer(
  ({ tariffProfileId }: DetailsTariffProfileProps) => {
    const [tariffProfileGridData, setTariffProfileGridData] =
      useState<TTariffProfileGrid | null>(null);
    // const changedRules = useInstance(() => new Map());
    const changedRules = useRef<CombinedKeyMap<
      RecordKeys,
      TariffProfileRulesRecord
    > | null>(null);

    const notify = usePageNotify();

    const changeCellState = useInstance(() => createChangeCellState());

    const initialValues: TTariffProfileMetaFormFields = {
      name: '',
      trainType: null,
      dateType: 'always',
      dateFrom: moment().startOf('day'),
      dateTo: moment().endOf('day'),
    };
    const [form] = Form.useForm();

    const getTariffProfileResource = useResource<
      TTariffProfile['id'],
      TTariffProfileGrid
    >({
      request: getTariffProfileRequest,
      errorNotify: notify,
      onDone(payload) {
        const data = prepareTariffProfileData(payload);

        form.setFieldsValue(data.tariffProfileMetaFields);
        setTariffProfileGridData(payload);
      },
    });

    const updateTariffProfileResource = useResource<
      TTariffProfile,
      TTariffProfileGrid
    >({
      request: updateTariffProfileRequest,
      errorNotify: notify,
      onDone() {
        changeCellState.clear();

        notify.clearAll();
        notify.addMessage({
          type: 'success',
          message: 'Изменения сохранены',
        });
      },
    });

    useEffect(() => {
      getTariffProfileResource.send(tariffProfileId);
    }, []);

    type RecordKeys = {
      terminalTypeId: string;
      tariffTypeId: string;
      zoneId: string;
    };
    const handleChangeRule = (
      dataMap: CombinedKeyMap<RecordKeys, TariffProfileRulesRecord>,
    ) => {
      changedRules.current = dataMap;

      console.log({ changedRules });
    };

    const handleSubmit = (fields: TTariffProfileMetaFormFields) => {
      if (!changedRules.current) return;

      const rules: TTariffProfileRule[] = Array.from(
        changedRules.current.values(),
        (record) => record.rule,
      ).filter(Boolean) as TTariffProfileRule[];

      console.log({ rules });

      if (fields?.dateType === 'always') {
        updateTariffProfileResource.send({
          id: tariffProfileId,
          name: fields.name,
          trainType: fields.trainType,
          rules,
        });

        return;
      }

      if (fields?.dateType === 'tripDate') {
        updateTariffProfileResource.send({
          id: tariffProfileId,
          name: fields.name,
          trainType: fields.trainType,
          fromTripDate: fields.dateFrom?.toJSON(),
          toTripDate: fields.dateTo?.toJSON(),
          rules,
        });
        return;
      }

      if (fields?.dateType === 'payDate') {
        updateTariffProfileResource.send({
          id: tariffProfileId,
          name: fields.name,
          trainType: fields.trainType,
          fromPayDate: fields.dateFrom?.toJSON(),
          toPayDate: fields.dateTo?.toJSON(),
          rules,
        });
        return;
      }
    };

    if (getTariffProfileResource.isLoading)
      return (
        <Card size="small">
          <Loading active />
        </Card>
      );

    if (!tariffProfileGridData) return null;

    return (
      <Loading active={updateTariffProfileResource.isLoading}>
        <Form
          layout="vertical"
          form={form}
          initialValues={initialValues}
          onFinish={handleSubmit}
        >
          <Card title="Общая информация" size="small">
            <div style={{ maxWidth: '50%' }}>
              <TariffProfileFields />
            </div>
          </Card>

          <Card title="Коэффициенты" size="small" style={{ marginTop: 16 }}>
            <ChangedCellsProvider state={changeCellState}>
              <DiscountRulesGrid
                data={tariffProfileGridData}
                onChange={handleChangeRule}
              />
            </ChangedCellsProvider>
          </Card>

          <CanAccess perm="PERM_TARIFF_PROFILE_UPDATE">
            <Card
              size="small"
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 16,
              }}
            >
              <Button type="primary" htmlType="submit">
                Сохранить
              </Button>
            </Card>
          </CanAccess>
        </Form>
      </Loading>
    );
  },
);
